import React, {useState} from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {Button, DropdownHandler, EMPHASIS, Icon, Tooltip} from '@forecast-it/design-system';

const Disabled = styled.span`
	display: block;
	opacity: 0.45;
	cursor: not-allowed;
`;

const Stop = styled.span`
	display: flex;
	flex: none;
`;

interface MenuActionProps {
	icon: 'edit' | 'trash';
	label: string;
	onSelect: () => void;
	/** When set, the item is disabled and this explains why. */
	blockedReason: string | null;
	dataCy: string;
}

const MenuAction = ({icon, label, onSelect, blockedReason, dataCy}: MenuActionProps) => {
	// The design-system Tooltip opens above its trigger and stays open while the pointer moves onto it, so on a disabled
	// item it would cover the item above and catch the pointer on its way there. It takes no side or hover option and
	// can't be closed from outside, so leaving the item remounts it, which closes it at once.
	const [tooltipKey, setTooltipKey] = useState(0);
	if (!blockedReason) {
		return (
			<DropdownHandler.Item onClick={onSelect} data-cy={dataCy}>
				<Icon icon={icon} />
				{label}
			</DropdownHandler.Item>
		);
	}
	return (
		<Tooltip key={tooltipKey} content={blockedReason} width="240px">
			<Disabled onPointerLeave={() => setTooltipKey(k => k + 1)}>
				<DropdownHandler.Item disabled onClick={() => undefined} data-cy={dataCy}>
					<Icon icon={icon} />
					{label}
				</DropdownHandler.Item>
			</Disabled>
		</Tooltip>
	);
};

export interface VersionMenuProps {
	name: string;
	/** False while this is the active version and no other version could take over as active. */
	canDelete: boolean;
	onRename: () => void;
	onDelete: () => void;
}

/**
 * The ⋯ menu on a version's compare column: Rename and Delete version. Delete is disabled, with the reason, on the active
 * version when no other version can become active. Standard has no menu.
 */
export const VersionMenu = ({name, canDelete, onRename, onDelete}: VersionMenuProps) => {
	const {formatMessage} = useIntl();
	const deleteBlocked = canDelete ? null : formatMessage({id: 'baseline.versions.active_delete_tip'});
	return (
		<Stop onClick={e => e.stopPropagation()}>
			<DropdownHandler>
				<DropdownHandler.Trigger>
					<Button
						emphasis={EMPHASIS.LOW}
						icon="more"
						aria-label={formatMessage({id: 'baseline.versions.more_actions'}, {name})}
						data-cy={`baseline-version-menu-${name}`}
					/>
				</DropdownHandler.Trigger>
				<DropdownHandler.Content offset={4}>
					<MenuAction
						icon="edit"
						label={formatMessage({id: 'baseline.versions.rename'})}
						onSelect={onRename}
						blockedReason={null}
						dataCy="baseline-version-menu-rename"
					/>
					<MenuAction
						icon="trash"
						label={formatMessage({id: 'baseline.versions.delete_version'})}
						onSelect={onDelete}
						blockedReason={deleteBlocked}
						dataCy="baseline-version-menu-delete"
					/>
				</DropdownHandler.Content>
			</DropdownHandler>
		</Stop>
	);
};
